// Shared loading + error UX for social sign-in bridge controllers.
//
// Both bridge--google-sign-in and bridge--apple-sign-in delegate their
// button visual state to this module:
//
//   1. Spinner + label the moment the user taps (iOS sheet present is
//      sometimes a noticeable beat on cold start).
//   2. Peer auth controls disabled while in flight (the other provider
//      button + the email/password form).
//   3. On error, restore + dismissable toast. On user-cancel, restore
//      silently — cancel is not a failure.
//   4. 45s hard timeout safety net.
//
// Cache hygiene is handled by turbo-cache-control=no-cache on the
// sign-in/up views — sign-in pages should never be cached anyway, so
// there is no stale-snapshot problem to defend against here.

const TIMEOUT_MS = 45_000

const SPINNER_SVG = `
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
       xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
       class="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5"
            stroke-opacity="0.18"/>
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round"/>
  </svg>
`

// Selectors for "peer" inputs we soft-disable while a sign-in is mid-flight.
// Intentionally narrow: other provider buttons + email/password form. We do
// NOT touch language toggle, links, etc.
const PEER_SELECTOR = [
  "[data-controller~='bridge--google-sign-in']",
  "[data-controller~='bridge--apple-sign-in']",
  "form[action$='/sign_in'] :is(input, button)",
  "form[action$='/sign_up'] :is(input, button)"
].join(",")

export function beginLoading(controller, { label }) {
  const button = controller.element
  if (controller._signInLoading) return

  controller._signInLoading = true
  controller._signInOriginalHTML = button.innerHTML
  controller._signInOriginalDisabled = button.disabled

  button.innerHTML = `
    <span class="inline-flex items-center justify-center gap-2 leading-none">
      ${SPINNER_SVG}
      <span class="text-[13px] font-bold tracking-wide">${escapeHTML(label)}</span>
    </span>
  `
  button.disabled = true
  button.setAttribute("aria-busy", "true")
  button.classList.add("opacity-90", "cursor-progress")

  controller._signInPeers = disablePeers(button)

  controller._signInTimeout = setTimeout(() => {
    endLoading(controller, {
      error: true,
      errorMessage: timeoutMessage()
    })
  }, TIMEOUT_MS)
}

export function cancelTimeout(controller) {
  if (controller._signInTimeout) {
    clearTimeout(controller._signInTimeout)
    controller._signInTimeout = null
  }
}

export function endLoading(controller, { error = false, errorMessage = null } = {}) {
  cancelTimeout(controller)

  if (!controller._signInLoading) {
    if (error && errorMessage) showAuthError(errorMessage)
    return
  }

  const button = controller.element
  button.innerHTML = controller._signInOriginalHTML
  button.disabled = controller._signInOriginalDisabled === true
  button.removeAttribute("aria-busy")
  button.classList.remove("opacity-90", "cursor-progress")

  restorePeers(controller._signInPeers || [])

  controller._signInLoading = false
  controller._signInOriginalHTML = null
  controller._signInOriginalDisabled = null
  controller._signInPeers = null

  if (error && errorMessage) showAuthError(errorMessage)
}

export function showAuthError(message) {
  const host = document.getElementById("toast-notifications")
  if (!host) {
    // Fallback: layout doesn't have the toast region (shouldn't happen on
    // the mobile sign-in layout, but be defensive). Console-only is fine —
    // the server-side flash partial will catch the next render.
    console.warn("[social-sign-in] toast host missing:", message)
    return
  }

  // Wrapper div is what toast-manager-controller's MutationObserver expects
  // to remove (it walks up to the direct child of the toast host).
  const slot = document.createElement("div")
  slot.innerHTML = buildErrorToastMarkup(message)
  host.appendChild(slot)
}

// ── private ───────────────────────────────────────────────────────────────

function disablePeers(originButton) {
  const touched = []
  document.querySelectorAll(PEER_SELECTOR).forEach((el) => {
    if (el === originButton) return
    const wasDisabled = "disabled" in el ? el.disabled : null
    if ("disabled" in el) el.disabled = true
    el.classList.add("opacity-50", "pointer-events-none")
    touched.push({ el, wasDisabled })
  })
  return touched
}

function restorePeers(peers) {
  peers.forEach(({ el, wasDisabled }) => {
    if (wasDisabled !== null && "disabled" in el) {
      el.disabled = wasDisabled
    }
    el.classList.remove("opacity-50", "pointer-events-none")
  })
}

function buildErrorToastMarkup(message) {
  const safe = escapeHTML(message)
  return `
    <div data-toast-manager-target="toast"
         data-action="click->toast-manager#dismiss"
         role="alert"
         class="inline-flex items-center gap-2.5 px-4 py-3 bg-base-100 rounded-full
                shadow-[0_8px_32px_rgba(15,23,42,0.12),0_2px_8px_rgba(15,23,42,0.06)]
                border border-base-300/80 cursor-pointer pointer-events-auto">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="flex-shrink-0">
        <circle cx="12" cy="12" r="10" stroke="#EF4444" stroke-width="1.5" class="toast-icon-circle"/>
        <path d="M14.5 9.5l-5 5M9.5 9.5l5 5" stroke="#EF4444" stroke-width="1.5"
              stroke-linecap="round" class="toast-icon-x"/>
      </svg>
      <p class="text-[13px] text-base-content font-semibold leading-tight">${safe}</p>
    </div>
  `
}

function escapeHTML(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function timeoutMessage() {
  return (
    document.documentElement.dataset.signInTimeoutMessage ||
    "Sign-in is taking longer than expected. Please try again."
  )
}
